<template>
  <section class="admin-page" v-if="creator">

    <!-- Header -->
    <header class="admin-header">
      <div>
        <h1>Creator Review</h1>
        <p>Detailed profile, risk signals, and approval controls.</p>
      </div>

      <div class="header-actions">
        <button
          v-if="creator.status === 'pending' ||
          creator.status === 'suspended'"
          class="primary-btn"
          @click="approve"
        >
          Approve Creator
        </button>

        <button
          v-if="creator.status === 'active'"
          class="danger-btn"
          @click="suspend"
        >
          Suspend
        </button>
        
      </div>
    </header>

    <!-- Summary -->
    <section class="summary-grid">

      <div class="card">
        <p class="label">Creator</p>
        <h3>{{ creator.name }}</h3>
        <p class="muted">{{ creator.email }}</p>
      </div>

      <div class="card">
        <p class="label">Platform</p>
        <h3>{{ creator.platform || '—' }}</h3>
        <p class="muted">{{ creator.followers || 0 }} followers</p>
      </div>

      <div class="card">
        <p class="label">Status</p>
        <span :class="['status-pill', creator.status]">
          {{ creator.status }}
        </span>
      </div>

      <div class="card">
        <p class="label">Risk Score</p>
        <h3 :class="riskClass">{{ creator.riskScore }}%</h3>
        <p class="muted">Automated trust assessment</p>
      </div>

    </section>

    <!-- Details -->
    <section class="details-layout">

      <!-- LEFT -->
      <div class="card">
        <h3>Identity & Verification</h3>

        <div class="info-row">
          <span>Email Verified</span>
          <span class="status-pill ok">Yes</span>
        </div>

        <div class="info-row">
          <span>Phone Verified</span>
          <span class="status-pill ok">Yes</span>
        </div>

        <div class="info-row">
          <span>Social Verified</span>
          <span class="status-pill partial">
            {{ creator.platform || "Pending" }}
          </span>
        </div>

        <div class="info-row">
          <span>Joined</span>
          <strong>{{ creator.joined }}</strong>
        </div>
      </div>

      <!-- RIGHT -->
      <div class="card">
        <h3>Activity Overview</h3>

        <div class="metric">
          <span>Total Audience</span>
          <strong>{{ activity.audience }}</strong>
        </div>

        <div class="metric">
          <span>Broadcasts Sent</span>
          <strong>{{ activity.broadcasts }}</strong>
        </div>

        <div class="metric">
          <span>Delivery Rate</span>
          <strong>{{ activity.delivery }}%</strong>
        </div>

        <div class="metric">
          <span>Last Broadcast</span>
          <strong>{{ activity.lastBroadcast }}</strong>
        </div>
      </div>

    </section>

    <!-- Risk Flags -->
    <section class="card">
      <h3>Risk Signals</h3>

      <ul class="risk-list">
        <li v-for="flag in riskFlags" :key="flag">
          {{ flag }}
        </li>
      </ul>

      <p v-if="riskFlags.length === 0" class="muted">
        No risk signals detected.
      </p>
    </section>

  </section>
</template>

<script>
import { useToast } from "vue-toastification";
import {
  getCreatorDetail,
  approveCreator,
  suspendCreator
} from "../apis/infapis";

export default {
  name: "AdminCreatorDetail",

  data() {
    return {
      toast: useToast(),

      creator: null,

      activity: {
        audience: 0,
        broadcasts: 0,
        delivery: 0,
        lastBroadcast: "—"
      },

      riskFlags: []
    };
  },

  computed: {
    riskClass() {
      if (!this.creator) return "";

      if (this.creator.riskScore < 25) return "ok";
      if (this.creator.riskScore < 60) return "warn";
      return "danger";
    }
  },

  async mounted() {
    await this.loadCreator();
  },

  methods: {

    async loadCreator() {

      const creator_id = this.$route.params.id;

      // alert(creator_id+"");

      const res = await getCreatorDetail(creator_id);

      if (!res.status) {
        this.toast.error(res.success || "Failed to load creator");
        return;
      }

      this.creator = {
        user_id: res.creator.user_id,
        name: res.creator.name,
        email: res.creator.email,
        status: res.creator.status,
        riskScore: res.creator.riskScore,
        trustScore: res.creator.trustScore,
        platform: res.creator.platform,
        followers: res.creator.followers,
        joined: res.creator.joined
      };

      this.activity = res.activity;
      this.riskFlags = res.riskFlags || [];
    },

    async approve() {
      const ldataRaw = sessionStorage.getItem("ldata");
      const ldata = ldataRaw ? JSON.parse(ldataRaw) : null;
      const res = await approveCreator(this.creator.user_id,ldata.user_id);

      if (!res.status) {
        this.toast.error(res.success || "Approval failed");
        return;
      }

      this.toast.success("Creator approved");

      this.creator.status = "active";
    },

    async suspend() {
      const ldataRaw = sessionStorage.getItem("ldata");
      const ldata = ldataRaw ? JSON.parse(ldataRaw) : null;
      const res = await suspendCreator(this.creator.user_id,ldata.user_id);

      if (!res.status) {
        this.toast.error(res.success || "Suspend failed");
        return;
      }

      this.toast.success("Creator suspended");

      this.creator.status = "suspended";
    }
  }
};
</script>


<style scoped>
.admin-page {
  max-width: 1440px;
  margin: auto;
  padding: 48px;
}

.admin-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-end;
}

.admin-header h1 {
  font-size: 36px;
  font-weight: 800;
}

.admin-header p {
  color: #64748b;
}

.header-actions {
  display: flex;
  gap: 12px;
}

/* Summary */
.summary-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 24px;
  margin: 40px 0;
}

.card {
  background: white;
  border-radius: 20px;
  padding: 24px;
  border: 1px solid #e5e7eb;
}

.label {
  font-size: 11px;
  text-transform: uppercase;
  color: #94a3b8;
  font-weight: 700;
}

.muted {
  font-size: 13px;
  color: #64748b;
}

/* Details */
.details-layout {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 24px;
  margin-bottom: 40px;
}

.info-row,
.metric {
  display: flex;
  justify-content: space-between;
  padding: 12px 0;
  border-bottom: 1px solid #f1f5f9;
}

/* Risk */
.risk-list {
  margin-top: 16px;
  padding-left: 18px;
}

.risk-list li {
  font-size: 14px;
  margin-bottom: 8px;
}

/* Pills */
.status-pill {
  font-size: 11px;
  padding: 4px 10px;
  border-radius: 999px;
  font-weight: 800;
  text-transform: uppercase;
}

.status-pill.pending {
  background: #fffbeb;
  color: #92400e;
}

.status-pill.active,
.ok {
  background: #ecfdf5;
  color: #047857;
}

.status-pill.suspended,
.danger {
  background: #fef2f2;
  color: #991b1b;
}

.warn {
  background: #fffbeb;
  color: #92400e;
}

/* Buttons */
.primary-btn {
  background: #0f172a;
  color: white;
  padding: 12px 20px;
  border-radius: 14px;
  font-weight: 700;
}

.danger-btn {
  background: #991b1b;
  color: white;
  padding: 12px 20px;
  border-radius: 14px;
  font-weight: 700;
}

/* Mobile */
@media (max-width: 900px) {
  .summary-grid,
  .details-layout {
    grid-template-columns: 1fr;
  }

  .admin-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 16px;
  }

  .admin-page {
    padding: 24px;
  }
}
</style>
